import {
  ApolloClient,
  ApolloLink,
  FetchResult,
  from,
  fromPromise,
  InMemoryCache,
  NextLink,
  Observable,
  Operation,
} from "@apollo/client";
import { BatchHttpLink } from "@apollo/client/link/batch-http";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { getTenantHeaderValue } from "@aurem/utils";
import { createUploadLink } from "apollo-upload-client";
import { getCurrentLanguage } from "providers/I18nProvider";
import type { AuthState, AuthTokens } from "../AuthProvider";
import { CACHE_CONFIG } from "./cache";
import { checkJWTExpired, createNewTokens } from "./utils";

const API_URL = process.env.REACT_APP_GRAPHQL_API ?? "";

export interface ApolloClientConfig {
  onSessionExpired: () => void;
  onLogout: () => void;
  onTokenRefresh?: (tokens: AuthTokens) => void;
}

class ApolloClientManager {
  private client: ApolloClient | null = null;
  private authState: AuthTokens | null = null;
  private config: ApolloClientConfig | null = null;
  private isRefreshing = false;
  private pendingRequests: Array<() => void> = [];

  private async getFreshAccessToken(): Promise<string | null> {
    if (this.authState == null) {
      return null;
    }

    if (
      this.authState.accessToken &&
      !checkJWTExpired(this.authState.accessToken)
    ) {
      return this.authState.accessToken;
    }

    if (this.authState.refreshToken) {
      if (this.isRefreshing) {
        await new Promise<void>((resolve) => this.queueRequest(resolve));
        return this.authState?.accessToken ?? null;
      }

      this.isRefreshing = true;

      try {
        const newTokens = await this.refreshTokens();
        if (newTokens) {
          this.authState = newTokens;
          this.config?.onTokenRefresh?.(newTokens);
        } else {
          this.config?.onLogout();
        }
        this.resolvePendingRequests();
        return newTokens?.accessToken ?? null;
      } catch (error) {
        this.resolvePendingRequests();
        this.config?.onLogout();
        throw error;
      } finally {
        this.isRefreshing = false;
      }
    }

    return null;
  }

  private async refreshTokens(): Promise<AuthTokens | null> {
    if (!this.authState?.refreshToken) {
      return null;
    }
    return await createNewTokens(this.authState.refreshToken);
  }

  private queueRequest(callback: () => void): void {
    this.pendingRequests.push(callback);
  }

  private resolvePendingRequests(): void {
    this.pendingRequests.forEach((cb) => cb());
    this.pendingRequests = [];
  }

  private createAuthLink(): ApolloLink {
    return setContext(async (_, { headers }) => {
      const token = await this.getFreshAccessToken();
      if (token) {
        return {
          headers: {
            ...headers,
            authorization: token,
            "Accept-Language": getCurrentLanguage(),
            "x-tenant-id": getTenantHeaderValue(),
          },
        };
      }
      return {
        headers: {
          ...headers,
          "Accept-Language": getCurrentLanguage(),
          "x-tenant-id": getTenantHeaderValue(),
        },
      };
    });
  }

  private createErrorLink(): ApolloLink {
    return onError(({ forward, graphQLErrors = [], operation }) => {
      for (const err of graphQLErrors) {
        const { extensions, message } = err;
        const code = extensions?.code;

        if (code === "INTERNAL_SERVER_ERROR") {
          if (message === "Access token expired") {
            return this.refreshTokenAndRetry(forward, operation);
          }
          if (message === "jwt expired") {
            this.handleSessionExpired();
            return;
          }
        } else if (code === "UNAUTHORIZED") {
          if (
            message === "User session not found" ||
            message === "Session expired due to inactivity"
          ) {
            this.config?.onLogout();
            return;
          }
        } else if (code === "FORBIDDEN") {
          if (message === "Access token is missing") {
            return this.waitForTokenAndRetry(forward, operation);
          }
        }
      }
    });
  }

  private createUploadLink(): ReturnType<typeof createUploadLink> {
    return createUploadLink({
      uri: API_URL,
    });
  }

  private createBatchLink(): BatchHttpLink {
    return new BatchHttpLink({
      uri: API_URL,
      batchMax: 5,
      batchInterval: 20,
    });
  }

  private createMediatorLink(
    uploadLink: ReturnType<typeof createUploadLink>,
    batchLink: BatchHttpLink,
  ): ApolloLink {
    const containsMutationOrExcluded = (operation: Operation) => {
      return (
        !!operation.query.definitions.find(
          (it) => "operation" in it && it.operation === "mutation",
        ) || operation.getContext().excludeFromBatch
      );
    };

    return ApolloLink.split(containsMutationOrExcluded, uploadLink, batchLink);
  }

  private refreshTokenAndRetry(
    forward: NextLink,
    operation: Operation,
  ): Observable<FetchResult> | void {
    if (this.authState?.refreshToken == null) return;

    if (!this.isRefreshing) {
      return fromPromise(
        this.refreshTokens()
          .then((newTokens) => {
            if (newTokens == null) return;
            this.authState = newTokens;
            try {
              this.config?.onTokenRefresh?.(newTokens);
            } catch (_) {
              //
            }
          })
          .catch(() => {
            this.resolvePendingRequests();
            this.config?.onLogout();
          }),
      ).flatMap(() => {
        return forward(operation);
      });
    } else {
      return fromPromise(
        new Promise<void>((res) => this.queueRequest(res)),
      ).flatMap(() => {
        return forward(operation);
      });
    }
  }

  private waitForTokenAndRetry(
    forward: NextLink,
    operation: Operation,
  ): Observable<FetchResult> | void {
    if (this.authState?.refreshToken == null) return;

    if (this.isRefreshing) {
      return fromPromise(
        new Promise<void>((res) => this.queueRequest(res)),
      ).flatMap(() => {
        return forward(operation);
      });
    } else {
      return forward(operation);
    }
  }

  private handleSessionExpired(): void {
    this.config?.onSessionExpired();
    this.config?.onLogout();
  }

  public initialize(config: ApolloClientConfig): ApolloClient {
    if (this.client) {
      return this.client;
    }

    this.config = config;

    const errorLink = this.createErrorLink();
    const authLink = this.createAuthLink();
    const uploadLink = this.createUploadLink();
    const batchLink = this.createBatchLink();
    const mediatorLink = this.createMediatorLink(uploadLink, batchLink);

    this.client = new ApolloClient({
      link: from([errorLink, authLink, mediatorLink]),
      cache: new InMemoryCache(CACHE_CONFIG),
      devtools: {
        enabled: process.env.NODE_ENV === "development",
      },
      defaultOptions: {
        watchQuery: {
          fetchPolicy: "cache-and-network",
          nextFetchPolicy: "cache-and-network",
        },
      },
    });

    return this.client;
  }

  public updateAuth(authState: AuthState): void {
    this.authState = {
      accessToken: authState?.accessToken,
      refreshToken: authState?.refreshToken,
    };
  }

  public async reset(): Promise<void> {
    if (!this.client) return;

    await this.client.clearStore();
    this.authState = null;
    this.isRefreshing = false;
    this.pendingRequests = [];
  }
}

export const apolloClientManager = new ApolloClientManager();
